import React, { useEffect, useRef, useState } from "react";
import { useProductsContext } from "../context/products_context";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { formatPrice } from "../utils/helpers";
import { stagger, blurUp, rise, ruleGrow } from "../utils/motion";
import Error from "./Error";
import Loading from "./Loading";

const headVariants = blurUp;
const trackVariants = stagger(0.12, 0.1);
const itemVariants = rise;

const GAP_PX = 20;

const OverlayCard = ({ img, name, price, weekendPrice, category }) => (
  <Link to={`/products/${name}`} className="o-card">
    <img src={img} alt={name} loading="lazy" />
    <div className="shade" aria-hidden="true" />
    {weekendPrice ? (
      <span className="badge">víkend od {formatPrice(weekendPrice)}</span>
    ) : null}
    <div className="meta">
      {category ? <span className="cat">{category}</span> : null}
      <h5>{name}</h5>
      <p>
        <em>od</em> {formatPrice(price)}
      </p>
    </div>
    <span className="hover-arrow" aria-hidden="true">
      <FaArrowRight />
    </span>
  </Link>
);

const FeaturedProducts = () => {
  const {
    products_loading: loading,
    products_error: error,
    featured_products: featured,
  } = useProductsContext();

  const trackRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [current, setCurrent] = useState(1);
  const count = featured.length;

  const getStep = () => {
    const el = trackRef.current;
    if (!el || !el.firstElementChild) return 0;
    return el.firstElementChild.getBoundingClientRect().width + GAP_PX;
  };

  // Track renders the list 3× — user scrolls the middle copy; crossing into an
  // outer copy teleports back by one copy width, which is invisible because
  // the content repeats exactly.
  const updateSlider = () => {
    const el = trackRef.current;
    const step = getStep();
    if (!el || !step || !count) return;
    const copyWidth = step * count;

    if (el.scrollLeft < copyWidth * 0.5) {
      el.scrollLeft += copyWidth;
    } else if (el.scrollLeft > copyWidth * 1.5) {
      el.scrollLeft -= copyWidth;
    }

    const idx =
      ((Math.round(el.scrollLeft / step) % count) + count) % count;
    setCurrent(idx + 1);
    setProgress(count > 1 ? idx / (count - 1) : 1);
  };

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    // start at the middle copy
    const raf = requestAnimationFrame(() => {
      el.scrollLeft = getStep() * count;
      updateSlider();
    });
    window.addEventListener("resize", updateSlider);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", updateSlider);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  const scrollByCard = (dir) => {
    const el = trackRef.current;
    const step = getStep();
    if (!el || !step) return;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  if (loading) {
    return <Loading />;
  }
  if (error) {
    return <Error />;
  }

  return (
    <Wrapper className="section">
      <span className="watermark" aria-hidden="true">
        Oblíbené
      </span>
      <div className="section-center">
        <motion.header
          className="sec-head"
          variants={headVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.6 }}
        >
          <div>
            <span className="kicker">
              <motion.span className="rule" aria-hidden="true" variants={ruleGrow} />
              Vybíráme pro vás
            </span>
            <h2>
              Oblíbené <em>produkty</em>
            </h2>
          </div>
          <Link to="/pronajem-party-stanu-vybaveni" className="head-link">
            Všechny produkty
            <FaArrowRight aria-hidden="true" />
          </Link>
        </motion.header>

        <div className="slider-zone">
          <button
            type="button"
            className="nav-arrow prev"
            onClick={() => scrollByCard(-1)}
            aria-label="Předchozí produkty"
          >
            <FaArrowLeft />
          </button>

          <motion.div
            className="slider-track"
            ref={trackRef}
            onScroll={updateSlider}
            variants={trackVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
          >
            {[0, 1, 2].map((copy) =>
              featured.map((product) => (
                <motion.div
                  key={`${product.id}-${copy}`}
                  className="slide"
                  variants={copy === 1 ? itemVariants : undefined}
                >
                  <OverlayCard {...product} />
                </motion.div>
              ))
            )}
          </motion.div>

          <button
            type="button"
            className="nav-arrow next"
            onClick={() => scrollByCard(1)}
            aria-label="Další produkty"
          >
            <FaArrowRight />
          </button>
        </div>

        <div className="slider-meta">
          <div className="progress" aria-hidden="true">
            <span style={{ transform: `scaleX(${progress})` }} />
          </div>
          <span className="counter">
            0{current} <span className="of">/ 0{featured.length}</span>
          </span>
        </div>

        <div className="cta-wrap">
          <Link to="/pronajem-party-stanu-vybaveni" className="ghost-btn">
            Všechny produkty
          </Link>
        </div>
      </div>
    </Wrapper>
  );
};

const Wrapper = styled.section`
  background: var(--clr-cream);
  position: relative;
  overflow: hidden;

  .watermark {
    position: absolute;
    right: -1.5rem;
    top: 1rem;
    font-family: var(--ff-script);
    font-size: clamp(6rem, 16vw, 12rem);
    line-height: 1;
    color: transparent;
    -webkit-text-stroke: 1px rgba(184, 115, 51, 0.16);
    pointer-events: none;
    user-select: none;
  }

  .sec-head {
    position: relative;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 1.5rem;
    margin-bottom: 2.75rem;
  }
  .kicker {
    display: inline-flex;
    align-items: center;
    gap: 0.9rem;
    font-family: var(--ff-body);
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.22em;
    color: var(--clr-accent-dark);
    margin-bottom: 1rem;
  }
  .kicker .rule {
    display: inline-block;
    width: 40px;
    height: 1px;
    background: var(--clr-accent);
    transform-origin: left;
  }
  h2 {
    font-size: clamp(2rem, 4.5vw, 3.1rem);
    letter-spacing: -0.015em;
    margin: 0;
  }
  h2 em {
    font-style: italic;
    font-weight: 500;
    color: var(--clr-accent-dark);
  }

  .head-link {
    display: none;
    align-items: center;
    gap: 0.6rem;
    font-family: var(--ff-body);
    font-weight: 600;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--clr-charcoal-1);
    white-space: nowrap;
    padding-bottom: 0.5rem;
    transition: var(--transition);
    svg {
      font-size: 0.8rem;
      transition: var(--transition);
      color: var(--clr-accent);
    }
    &:hover {
      color: var(--clr-accent-dark);
      svg {
        transform: translateX(4px);
      }
    }
  }

  /* ---- slider with flanking arrows ---- */
  .slider-zone {
    position: relative;
  }

  .nav-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 3;
    width: 46px;
    height: 46px;
    border-radius: 50%;
    border: 1px solid var(--clr-accent);
    background: var(--clr-cream);
    color: var(--clr-accent-dark);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.95rem;
    cursor: pointer;
    box-shadow: var(--shadow);
    transition: var(--transition);
  }
  .nav-arrow.prev {
    left: -8px;
  }
  .nav-arrow.next {
    right: -8px;
  }
  .nav-arrow:hover {
    background: var(--clr-accent);
    color: var(--clr-paper);
    transform: translateY(-50%) scale(1.06);
  }

  .slider-track {
    display: flex;
    gap: ${GAP_PX}px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }
  .slider-track::-webkit-scrollbar {
    display: none;
  }
  .slide {
    flex: 0 0 86%;
    scroll-snap-align: start;
    min-width: 0;
  }

  /* ---- card ---- */
  .o-card {
    position: relative;
    display: block;
    border-radius: var(--radius-lg);
    overflow: hidden;
    aspect-ratio: 4 / 3;
    background: var(--clr-cream-warm);
    isolation: isolate;
  }
  .o-card::after {
    content: "";
    position: absolute;
    inset: 10px;
    border: 1px solid rgba(250, 244, 234, 0.45);
    border-radius: calc(var(--radius-lg) - 6px);
    opacity: 0;
    transition: var(--transition);
    pointer-events: none;
  }
  .o-card:hover::after {
    opacity: 1;
  }
  .o-card img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 600ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  .o-card .shade {
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(20, 13, 9, 0.9) 0%, rgba(20, 13, 9, 0.3) 36%, transparent 58%);
    transition: var(--transition);
  }
  .o-card:hover img {
    transform: scale(1.06);
  }
  .o-card:hover .shade {
    background: linear-gradient(to top, rgba(20, 13, 9, 0.94) 0%, rgba(20, 13, 9, 0.45) 42%, rgba(20, 13, 9, 0.12) 72%);
  }
  .o-card:hover .hover-arrow {
    opacity: 1;
    transform: translate(0, 0);
  }

  .o-card .badge {
    position: absolute;
    top: 0.9rem;
    left: 0.9rem;
    background: rgba(250, 244, 234, 0.92);
    color: var(--clr-charcoal-1);
    font-family: var(--ff-body);
    font-size: 0.68rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    padding: 0.3rem 0.65rem;
    border-radius: var(--radius-pill);
  }

  .o-card .meta {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 1.3rem 1.5rem 1.35rem;
  }
  .o-card .meta .cat {
    display: block;
    font-family: var(--ff-body);
    font-size: 0.66rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.18em;
    color: rgba(250, 244, 234, 0.65);
    margin-bottom: 0.35rem;
  }
  .o-card .meta h5 {
    font-family: var(--ff-display);
    font-size: 1.3rem;
    font-weight: 600;
    color: var(--clr-cream);
    margin: 0 0 0.25rem;
    line-height: 1.2;
    letter-spacing: 0;
    text-transform: none;
  }
  .o-card .meta p {
    margin: 0;
    font-family: var(--ff-body);
    font-size: 0.92rem;
    font-weight: 600;
    color: var(--clr-accent-light);
  }
  .o-card .meta p em {
    font-family: var(--ff-display);
    font-style: italic;
    font-weight: 500;
    color: rgba(250, 244, 234, 0.75);
    margin-right: 0.15rem;
  }

  .o-card .hover-arrow {
    position: absolute;
    top: 0.9rem;
    right: 0.9rem;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--clr-accent);
    color: var(--clr-paper);
    opacity: 0;
    transform: translate(-6px, 6px);
    transition: var(--transition);
  }

  /* ---- progress + counter ---- */
  .slider-meta {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    margin-top: 1.75rem;
  }
  .progress {
    flex: 1;
    height: 2px;
    background: var(--clr-divider);
    border-radius: 2px;
    overflow: hidden;
  }
  .progress span {
    display: block;
    height: 100%;
    width: 100%;
    background: var(--clr-accent);
    transform-origin: left;
    transform: scaleX(0);
    transition: transform 250ms ease-out;
  }
  .counter {
    font-family: var(--ff-display);
    font-style: italic;
    font-size: 1.05rem;
    color: var(--clr-charcoal-1);
    letter-spacing: 0.04em;
    white-space: nowrap;
  }
  .counter .of {
    color: var(--clr-charcoal-3);
    font-size: 0.9rem;
  }

  .cta-wrap {
    display: flex;
    justify-content: center;
    margin-top: 2.25rem;
  }

  .ghost-btn {
    display: inline-block;
    background: transparent;
    border: 1px solid var(--clr-accent);
    color: var(--clr-accent-dark);
    padding: 0.7rem 1.7rem;
    text-transform: uppercase;
    font-family: var(--ff-body);
    font-weight: 600;
    font-size: 0.875rem;
    letter-spacing: 0.05em;
    border-radius: var(--radius-pill);
    transition: var(--transition);
    cursor: pointer;
  }
  .ghost-btn:hover {
    background: var(--clr-accent);
    color: var(--clr-paper);
  }

  @media (min-width: 576px) {
    .slide {
      flex-basis: calc((100% - ${GAP_PX}px) / 2);
    }
  }

  @media (min-width: 992px) {
    .head-link {
      display: inline-flex;
    }
    .slide {
      flex-basis: calc((100% - ${GAP_PX * 2}px) / 3);
    }
    .o-card {
      aspect-ratio: 3 / 2;
    }
    .cta-wrap {
      display: none;
    }
  }

  @media (min-width: 1280px) {
    .nav-arrow.prev {
      left: -23px;
    }
    .nav-arrow.next {
      right: -23px;
    }
  }
`;

export default FeaturedProducts;
